import { graphql } from "lib/generated/app-service-gql";
import { useGraphQlQuery } from "../useGraphQlClient";
import { UsageFilters, usageWindow } from "./usageWindow";

const companyAppActivityQuery = graphql(`
  query companyAppActivity($request: CompanyAppActivityRequest!) {
    companyAppActivity(request: $request) {
      days {
        date
        appUsers
        mcpUsers
      }
      activeUsers
      totalUsers
    }
  }
`);

/** Page loads were recorded for everyone from about this UTC day, so earlier days read low. */
export const APP_ACTIVITY_COMPLETE_FROM = "2026-08-17";

/** MCP use was recorded from part way through the day before, so this is the first full day. */
export const MCP_ACTIVITY_COMPLETE_FROM = "2026-10-01";

export type AppActivityDay = {
  /** UTC day as an ISO date, e.g. "2026-09-03". */
  date: string;
  /** Axis label, e.g. "3 Sep". */
  label: string;
  /** Tooltip label, e.g. "Thu 3 Sep". */
  longLabel: string;
  /** Users who loaded a page of the app. */
  appUsers: number;
  /** Users recorded using Stotles through MCP. */
  mcpUsers: number;
};

export type CompanyAppActivity = {
  /** One entry per day of the window, oldest first, gaps zero-filled. */
  days: AppActivityDay[];
  /** Users active in the app or through MCP on at least one day of the window. */
  activeUsers: number;
  /** Users the counts are drawn from: the company's, or the chosen team's. */
  totalUsers: number;
  /** Whether the window reaches back before page loads were recorded for everyone. */
  startsBeforeCompleteData: boolean;
  /** Whether the window reaches back before MCP use was recorded. */
  startsBeforeMcpData: boolean;
  isLoading: boolean;
  isError: boolean;
};

/**
 * The company's users active in the app and through MCP on each UTC day of the last `days` days,
 * today included.
 *
 * The server omits days nobody was active; a bar chart needs every day, so the window is rebuilt
 * here and the returned days slotted into it. Keyed on the UTC day, so a cached window is never
 * plotted against the next day's axis.
 */
export function useCompanyAppActivity(filters: UsageFilters): CompanyAppActivity {
  const { days } = filters;
  const { today, start, startsBefore } = usageWindow(days);
  const { data, isLoading, isError } = useGraphQlQuery(
    ["companyAppActivity", filters, today],
    companyAppActivityQuery,
    [{ request: filters }],
  );

  const activityByDate = new Map(
    (data?.companyAppActivity.days ?? []).map((day) => [day.date, day]),
  );

  const axis = Array.from({ length: days }, (_, index) => {
    const day = start.plus({ days: index });
    const date = day.toISODate() ?? "";
    return {
      date,
      label: day.toFormat("d LLL"),
      longLabel: day.toFormat("ccc d LLL"),
      appUsers: activityByDate.get(date)?.appUsers ?? 0,
      mcpUsers: activityByDate.get(date)?.mcpUsers ?? 0,
    };
  });

  return {
    days: axis,
    activeUsers: data?.companyAppActivity.activeUsers ?? 0,
    totalUsers: data?.companyAppActivity.totalUsers ?? 0,
    startsBeforeCompleteData: startsBefore(APP_ACTIVITY_COMPLETE_FROM),
    startsBeforeMcpData: startsBefore(MCP_ACTIVITY_COMPLETE_FROM),
    isLoading,
    isError,
  };
}
